/**
 * Canva page 2 (9/14) homepage. Header stays black + chalk in both modes.
 * Day page field is light gray; night inverts only the page field to black.
 * Tagline is the sage teal from that board, not the header color.
 */
// Lauren's Canva header pass (REQ-090): bar is #2B2B2B. The night page field stays #111111.
export const CANVA_NAV_BAR = '#2B2B2B';
export const CANVA_NAV_TITLE = '#E2DEDE';
export const CANVA_HOME_DAY_BG = '#E9E9E9';
export const CANVA_HOME_NIGHT_BG = '#111111';
export const CANVA_HOME_TAGLINE = '#429277';
export const CANVA_TITLE_FONT = 'Finger Paint';

/** @deprecated aliases */
export const CANVA_NAV_DAY_BAR = CANVA_NAV_BAR;
export const CANVA_NAV_DAY_TITLE = CANVA_NAV_TITLE;
export const CANVA_NAV_NIGHT_BAR = CANVA_NAV_BAR;
export const CANVA_NAV_NIGHT_TITLE = CANVA_NAV_TITLE;
export const CANVA_NAV_BLUE = CANVA_NAV_BAR;
export const CANVA_TITLE_CREAM = CANVA_NAV_TITLE;

export function canvaHeaderChrome(_isDarkMode) {
  return { bar: CANVA_NAV_BAR, title: CANVA_NAV_TITLE, icon: CANVA_NAV_TITLE };
}

export function canvaHomeTaglineColor(_isDarkMode) {
  return CANVA_HOME_TAGLINE;
}

export function canvaHomePageBg(isDarkMode) {
  return isDarkMode ? CANVA_HOME_NIGHT_BG : CANVA_HOME_DAY_BG;
}

/** Hamburger colors sampled from Canva pages 1–2 (9/14). */
export const CANVA_MENU_BLUE = '#3F7EE6';
export const CANVA_MENU_MUTED = '#A1A49F';
export const CANVA_MENU_GREEN = '#2F9A84';
export const CANVA_MENU_ACCOUNT = '#E13421';
export const CANVA_MENU_FUND = '#DBA32D';
export const CANVA_MENU_ADMIN = '#E85A9A';

export const CANVA_MENU_COLOR_BY_ID = {
  search: CANVA_MENU_BLUE,
  venues: CANVA_MENU_BLUE,
  add: CANVA_MENU_BLUE,
  grade: CANVA_MENU_BLUE,
  directory: CANVA_MENU_MUTED,
  events: CANVA_MENU_MUTED,
  gear: CANVA_MENU_MUTED,
  budget: CANVA_MENU_GREEN,
  calendar: CANVA_MENU_GREEN,
  map: CANVA_MENU_GREEN,
  account: CANVA_MENU_ACCOUNT,
  fund: CANVA_MENU_FUND,
  balance: CANVA_MENU_FUND,
  admin: CANVA_MENU_ADMIN,
};

export function canvaMenuItemColor(id) {
  return CANVA_MENU_COLOR_BY_ID[id] || '#ffffff';
}

export const HAMBURGER_ICON_BY_ID = {
  venues: 'business-outline',
  add: 'add-circle-outline',
  grade: 'ribbon-outline',
  directory: 'people-outline',
  events: 'ticket-outline',
  gear: 'swap-horizontal-outline',
  budget: 'wallet-outline',
  calendar: 'calendar-outline',
  map: 'map-outline',
  account: 'person-circle-outline',
  fund: 'heart-outline',
  balance: 'card-outline',
  admin: 'shield-outline',
  search: 'search-outline',
};

export function hamburgerIconForId(id) {
  return HAMBURGER_ICON_BY_ID[id] || 'ellipse-outline';
}

/** Hamburger / header items that are not live yet. */
export const NOTIFY_FEATURES = {
  directory: { title: 'Directory', routeName: 'CommunityList' },
  events: { title: 'Events', routeName: 'NotifyWhenLive' },
  gear: { title: 'Gear Swap', routeName: 'GearSwapList' },
  budget: { title: 'Budget', routeName: 'NotifyWhenLive' },
  calendar: { title: 'Calendar', routeName: 'MyCalendar' },
  map: { title: 'Map', routeName: 'TourPlanner' },
  account: { title: 'Account', routeName: 'Profile' },
  fund: { title: 'Fund', routeName: 'MutualAidFund' },
  balance: { title: 'Wallet', routeName: 'Wallet' },
};

export const GATED_ROUTE_FEATURES = {
  TourPlanner: 'map',
  MyCalendar: 'calendar',
  CalendarDayDetails: 'calendar',
  MutualAidFund: 'fund',
  Wallet: 'balance',
  Balance: 'balance',
  Profile: 'account',
  CommunityScreen: 'directory',
  CommunityList: 'directory',
  CommunityPostCreate: 'directory',
  ProfileCardEdit: 'directory',
  GearSwapList: 'gear',
  GearSwapPostCreate: 'gear',
  EventsList: 'directory',
  UserProfileScreen: 'directory',
  SharedTourView: 'map',
};

export function notifyTitleForFeature(id) {
  return NOTIFY_FEATURES[id]?.title || 'This feature';
}

export function isAdminUser(user) {
  return user?.userType === 'admin' || user?.isAdmin === true;
}

/**
 * Notify-me / ComingSoonOr items. Flip staging vs production in Admin
 * without another hosting deploy.
 */
export const COMING_SOON_FLAG_KEYS = {
  account: {
    staging: 'accountVisibleStaging',
    production: 'accountVisibleProduction',
    stagingDefault: true,
    productionDefault: true,
  },
  balance: {
    staging: 'walletVisibleStaging',
    production: 'walletVisibleProduction',
    stagingDefault: true,
    productionDefault: false,
  },
  fund: {
    staging: 'fundVisibleStaging',
    production: 'fundVisibleProduction',
    stagingDefault: true,
    productionDefault: false,
  },
  directory: {
    staging: 'communityVisibleStaging',
    production: 'communityVisibleProduction',
    stagingDefault: true,
    productionDefault: false,
  },
  events: {
    staging: 'eventsVisibleStaging',
    production: 'eventsVisibleProduction',
    stagingDefault: true,
    productionDefault: false,
  },
  gear: {
    staging: 'gearVisibleStaging',
    production: 'gearVisibleProduction',
    stagingDefault: true,
    productionDefault: false,
  },
  budget: {
    staging: 'expenseTrackerVisibleStaging',
    production: 'expenseTrackerVisibleProduction',
    stagingDefault: true,
    productionDefault: false,
  },
  calendar: {
    staging: 'calendarVisibleStaging',
    production: 'calendarVisibleProduction',
    stagingDefault: true,
    productionDefault: false,
  },
  map: {
    staging: 'tourPlannerVisibleStaging',
    production: 'tourPlannerVisibleProduction',
    stagingDefault: true,
    productionDefault: false,
  },
};

export function readLocalComingSoonUnlock() {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem('vrcUnlockComingSoon') === '1';
  } catch (_) {
    return false;
  }
}

/** Admins and the local unlock skip every Coming Soon gate. */
export function shouldBypassComingSoon(user) {
  return isAdminUser(user) || readLocalComingSoonUnlock();
}

export function isComingSoonFeatureUnlocked(featureId, {
  user,
  flags = {},
  deployTarget = 'production',
  localUnlock = false,
} = {}) {
  if (isAdminUser(user) || localUnlock) return true;
  const spec = COMING_SOON_FLAG_KEYS[featureId];
  if (!spec) return false;
  const staging = flags[spec.staging];
  const production = flags[spec.production];
  const stagingOn = (staging == null ? spec.stagingDefault : staging) === true;
  const productionOn = (production == null ? spec.productionDefault : production) === true;
  if (deployTarget === 'development' || deployTarget === 'staging') return stagingOn;
  return productionOn;
}

/** True when this user should see Notify When Live instead of the real screen. */
export function shouldGateComingSoon(user, featureId, options = {}) {
  if (shouldBypassComingSoon(user)) return false;
  if (!featureId) return true;
  return !isComingSoonFeatureUnlocked(featureId, { user, ...options });
}

export function isValidNotifyEmail(value) {
  const email = String(value || '').trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Build hamburger rows. Database / Add / Grade stay live.
 * Other rows are live when unlockedFeatures[id] is true (feature flags).
 */
export function buildHamburgerMenuItems({
  user,
  showTourCommunityHub = false,
  showCommunityNav = false,
  showExpenseTracker = false,
  showCalendarNav = true,
  showTourPlannerNav = true,
  isAdmin = false,
  isDevelopment = false,
  unlockedFeatures = {},
} = {}) {
  const rows = [
    { id: 'venues', title: 'Venue Database', live: true },
    { id: 'add', title: 'Add', live: true },
    { id: 'grade', title: 'Grade', live: true },
    // Lauren 2026-10-29: Directory and Map need an account (public: database, events, gear swap, fund).
    { id: 'directory', title: 'Directory', requiresSignIn: true },
    { id: 'events', title: 'Events' },
    { id: 'gear', title: 'Gear Swap' },
    { id: 'budget', title: 'Budget', requiresSignIn: true },
    { id: 'calendar', title: 'Calendar', locked: !showCalendarNav },
    { id: 'map', title: 'Map', requiresSignIn: true },
    { id: 'fund', title: 'Fund' },
    { id: 'balance', title: 'Wallet' },
    // Lauren 2026-10-21: Account sits below Fund and Wallet.
    { id: 'account', title: 'Account' },
  ];

  if (user && (isAdmin || isDevelopment)) {
    rows.push({ id: 'admin', title: 'Admin', live: true });
  }

  return rows.map((row) => {
    const live = row.live === true || isAdmin || unlockedFeatures[row.id] === true;
    return {
      ...row,
      color: canvaMenuItemColor(row.id),
      live,
      // Budget, Directory and Map are for signed-up users only: a live row asks guests to sign in.
      signInRequired: live && row.requiresSignIn === true && !user,
    };
  });
}
